import { Component, computed, DestroyRef, inject, OnInit, Signal, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { disabled, form, FormField, FormRoot, minLength, required, submit } from '@angular/forms/signals';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { UiButtonComponent } from '@app/shared/ui/button/ui-button.component';

import { LangToggleComponent } from '@app/shared/components/lang-toggle/lang-toggle.component';
import { passwordsMatch, passwordStrength } from '@app/shared/forms/rules';
import { AuthService } from '@app/shared/services/auth.service';
import { UiInputComponent } from '@app/shared/ui/input/ui-input.component';
import { TranslatePipe } from '@ngx-translate/core';
import { filter, take } from 'rxjs';

interface ConfirmEmailSetPasswordFormModel {
  newPassword: string;
  newPasswordConfirm: string;
}

interface SetPasswordResult {
  success: boolean;
  error: boolean;
}

@Component({
  imports: [RouterModule, LangToggleComponent, TranslatePipe, FormField, FormRoot, UiButtonComponent, UiInputComponent],
  selector: 'proffeo-password',
  templateUrl: './confirm-email-set-password.component.html'
})
export class AuthConfirmEmailSetPasswordComponent implements OnInit {
  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  private readonly authService: AuthService = inject(AuthService);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  /** Result of the (single) set-password request; `null` until the form has been sent. */
  private readonly request = signal<Signal<SetPasswordResult> | null>(null);

  private userIdParam: string;
  private tokenParam: string;

  /** The password can be set only once per link: after sending, the form stays locked whatever the outcome. */
  protected readonly requestSent = computed(() => this.request() !== null);
  protected readonly result = computed((): SetPasswordResult => this.request()?.() ?? { success: false, error: false });
  protected readonly showIncorrectLinkAlert = signal(false);
  protected readonly invalidLink = signal(false);
  protected readonly model = signal<ConfirmEmailSetPasswordFormModel>({ newPassword: '', newPasswordConfirm: '' });
  protected readonly form = form(this.model, path => {
    required(path.newPassword, { message: 'confirm-email-set-password-password-is-required' });
    minLength(path.newPassword, 8, { message: 'password-validation-message' });
    passwordStrength(path.newPassword, { message: 'password-validation-message' });
    required(path.newPasswordConfirm, { message: 'confirm-email-set-password-password-is-required' });
    passwordsMatch(path.newPasswordConfirm, path.newPassword, { message: 'confirm-password-does-not-match-password' });
    disabled(path, () => this.requestSent());
  });

  public ngOnInit(): void {
    this.route.queryParams
      .pipe(
        take(1),
        filter(params => {
          this.userIdParam = params['user'];
          this.tokenParam = params['token'];

          if (!this.userIdParam || !this.tokenParam) {
            this.showIncorrectLinkAlert.set(true);
            this.tokenParam = '';
            this.userIdParam = '';
            return false;
          }

          this.tokenParam = this.tokenParam.replaceAll(' ', '+');
          return true;
        })
      )
      .subscribe();

    this.authService
      .checkConfirmEmailToken({
        userId: this.userIdParam,
        token: this.tokenParam
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: () => this.invalidLink.set(false), error: () => this.invalidLink.set(true) });
  }

  protected onSubmitForm(): void {
    void submit(this.form, {
      action: async () => {
        this.request.set(
          this.authService.confirmEmailSetPassword({
            userId: this.userIdParam,
            token: this.tokenParam,
            password: this.model().newPassword
          })
        );
      }
    });
  }
}
